import './CategoryJourneyMatrix.scss';
import cn from 'classnames';
import { CSSProperties, useMemo, useRef, useState } from 'react';
import { Dropdown } from '@Components/Dropdown';
import { Placement, StretchMode } from '@Components/designConstants';
import { makeSteppedHeatMap, SequentialGreenRamp } from '@src/utils/colors';
import { CategoryRow } from '../category-pathing-types';
import { ValueMode, ValueModeType } from '../category-pathing-constants';
import { formatCompact, formatDays, formatInt, formatPercent } from '../utils/format';

const VALUE_MODE_OPTIONS = [
  { label: '% of Entry Category', value: ValueMode.PERCENT },
  { label: 'Actual Values', value: ValueMode.COUNT }
];

interface CategoryJourneyMatrixProps {
  categories: string[];
  categoryRows: CategoryRow[];
}

interface CellData {
  users: number;
  avgDays: number | null;
}

interface HoveredCell {
  row: string;
  col: string;
  users: number;
  avgDays: number | null;
  style: CSSProperties;
}

const TOOLTIP_W = 260;
const TOOLTIP_H = 140;
const TOOLTIP_PAD = 8;
const getClampedTooltipPosition = (
  mouseX: number,
  mouseY: number,
  container: HTMLDivElement | null
): CSSProperties => {
  let left = mouseX + 16;
  let top = mouseY + 16;
  if (container) {
    const r = container.getBoundingClientRect();
    left = Math.max(r.left + TOOLTIP_PAD, Math.min(left, r.right - TOOLTIP_W - TOOLTIP_PAD));
    top = Math.max(r.top + TOOLTIP_PAD, Math.min(top, r.bottom - TOOLTIP_H - TOOLTIP_PAD));
  }
  return { left, top };
};

export const CategoryJourneyMatrix = ({ categories, categoryRows }: CategoryJourneyMatrixProps) => {
  const [hovered, setHovered] = useState<HoveredCell | null>(null);
  const [valueMode, setValueMode] = useState<ValueModeType>(ValueMode.PERCENT);
  const containerRef = useRef<HTMLDivElement>(null);

  const cellMap = useMemo(() => {
    const m = new Map<string, CellData>();
    for (const r of categoryRows) {
      m.set(`${r.ntbCategory}|||${r.followupCategory}`, {
        users: r.ntbUsersWithFollowup,
        avgDays: r.avgDaysToFollowup
      });
    }
    return m;
  }, [categoryRows]);

  const rowTotals = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of categoryRows) {
      m.set(r.ntbCategory, (m.get(r.ntbCategory) ?? 0) + r.ntbUsersWithFollowup);
    }
    return m;
  }, [categoryRows]);

  const heatMap = useMemo(
    () => makeSteppedHeatMap({ min: 0, max: 100, stops: SequentialGreenRamp }),
    []
  );

  return (
    <div className='category-journey-matrix-section'>
      <div className='matrix-container' ref={containerRef}>
        <div className='matrix-controls'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            stretch={StretchMode.FIT}
            options={VALUE_MODE_OPTIONS}
            value={valueMode}
            onChange={setValueMode}
          />
        </div>
        <div className='matrix-axis-top'>Brand Halo Repurchase Category</div>
        <div className='matrix-body'>
          <div className='matrix-axis-left'>
            <span>Entry Category</span>
          </div>
          <div className='matrix-wrap'>
            <table className='journey-matrix' onMouseLeave={() => setHovered(null)}>
              <thead>
                <tr>
                  <th className='corner' aria-hidden='true' onMouseEnter={() => setHovered(null)} />
                  {categories.map(c => (
                    <th key={c} className='col-label' onMouseEnter={() => setHovered(null)}>
                      <span>{c}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map(rowCat => (
                  <tr key={rowCat}>
                    <th className='row-label' onMouseEnter={() => setHovered(null)}>
                      <div className='row-label-content'>{rowCat}</div>
                    </th>
                    {categories.map(colCat => {
                      const cell = cellMap.get(`${rowCat}|||${colCat}`);
                      const isDiagonal = rowCat === colCat;
                      const users = cell?.users ?? 0;
                      const rowTotal = rowTotals.get(rowCat) ?? 0;
                      const cellPct = rowTotal > 0 ? (users / rowTotal) * 100 : 0;
                      const heatInput = Math.sqrt(cellPct / 100) * 100;
                      const cellStyle =
                        users > 0 ? { backgroundColor: heatMap(heatInput) } : undefined;
                      const displayValue =
                        valueMode === ValueMode.PERCENT
                          ? formatPercent(cellPct)
                          : formatCompact(users);
                      return (
                        <td
                          key={colCat}
                          className={cn('matrix-cell', {
                            diagonal: isDiagonal,
                            empty: users === 0
                          })}
                          style={cellStyle}
                          onMouseEnter={e => {
                            if (!cell) {
                              setHovered(null);
                              return;
                            }
                            setHovered({
                              row: rowCat,
                              col: colCat,
                              users: cell.users,
                              avgDays: cell.avgDays,
                              style: getClampedTooltipPosition(
                                e.clientX,
                                e.clientY,
                                containerRef.current
                              )
                            });
                          }}
                          onMouseMove={e => {
                            if (!cell) return;
                            setHovered(h =>
                              h
                                ? {
                                    ...h,
                                    style: getClampedTooltipPosition(
                                      e.clientX,
                                      e.clientY,
                                      containerRef.current
                                    )
                                  }
                                : h
                            );
                          }}
                        >
                          {users > 0 ? (
                            <span className='cell-value'>{displayValue}</span>
                          ) : (
                            <span className='cell-empty'>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {hovered && (
              <div className='matrix-tooltip' style={hovered.style}>
                <div className='tooltip-title'>
                  {hovered.row} → {hovered.col}
                  <span
                    className={cn(
                      'tooltip-badge',
                      hovered.row === hovered.col ? 'repurchase' : 'cross-sell'
                    )}
                  >
                    {hovered.row === hovered.col ? 'Same Category' : 'Other Category'}
                  </span>
                </div>
                <div className='tooltip-row'>
                  <span>Brand Halo Repurchases</span>
                  <span>{formatInt(hovered.users)}</span>
                </div>
                <div className='tooltip-row'>
                  <span>% of {hovered.row} Repurchases</span>
                  <span>
                    {(() => {
                      const total = rowTotals.get(hovered.row) ?? 0;
                      return total > 0 ? `${((hovered.users / total) * 100).toFixed(1)}%` : '—';
                    })()}
                  </span>
                </div>
                <div className='tooltip-row'>
                  <span>Days to Repurchase</span>
                  <span>{formatDays(hovered.avgDays)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='matrix-legend'>
          <span>Share of entry category repurchase volume</span>
          <div className='legend-bar'>
            <span className='legend-tick'>0%</span>
            <div className='legend-gradient' />
            <span className='legend-tick'>100%</span>
          </div>
          <span className='legend-note'>Diagonal = same category</span>
        </div>
      </div>
    </div>
  );
};
